import {
  derivePullRequestWorkspaceView,
  readPullRequestWorkspaceSnapshot,
  writePullRequestWorkspaceSnapshot,
  type EnvironmentPullRequestRow,
  type PullRequestEnvironmentAnswer,
  type PullRequestEnvironmentFailure,
} from "@afar-research/client-runtime/state/pull-requests";
import { EnvironmentId, ProjectId } from "@afar-research/contracts";
import type {
  PullRequestInvolvement,
  PullRequestListFilters,
  PullRequestListInput,
  PullRequestListState,
} from "@afar-research/contracts";
import { useAtomValue } from "@effect/atom-react";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowDownUpIcon,
  CalendarArrowUpIcon,
  ClockIcon,
  EyeIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestIcon,
  LayersIcon,
  PenLineIcon,
  RefreshCwIcon,
  SearchIcon,
  TypeIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

import { isElectron } from "../env";
import { resolveShortcutCommand, shortcutLabelForCommand } from "../keybindings";
import { cn } from "../lib/utils";
import { pullRequestSurface, usePullRequestSurfaceStore } from "../pullRequestSurfaceStore";
import { Route, type PullRequestsWorkspaceSearch } from "../routes/_chat.pull-requests";
import { useProjects } from "../state/entities";
import { useEnvironments } from "../state/environments";
import { usePullRequestLists, usePullRequestListStats } from "../state/pullRequests";
import { primaryServerKeybindingsAtom } from "../state/server";
import { PanelLayoutControls } from "./chat/PanelLayoutControls";
import { PullRequestList } from "./PullRequestList";
import { projectPullRequestRepository } from "./pullRequest/linkPullRequestTarget.logic";
import {
  CompactFilterMenu,
  PullRequestFiltersMenu,
  PullRequestSearchInput,
  pullRequestProjectKey,
  type PullRequestFilterOption,
} from "./pullRequest/PullRequestListFilters";
import { collectPullRequestListFacets } from "./pullRequest/pullRequestListFacets.logic";
import { PullRequestSurfacePanel } from "./PullRequestSurfacePanel";
import { Button } from "./ui/button";
import { SidebarInset } from "./ui/sidebar";
import {
  WorkspaceBreadcrumb,
  WorkspaceBreadcrumbItem,
  WorkspaceBreadcrumbSeparator,
} from "./WorkspaceBreadcrumb";
import { WorkspacePageContainer } from "./WorkspacePageContainer";
import { WorkspacePageHeader } from "./WorkspacePageHeader";
import { WorkspaceShellMain } from "./WorkspaceShellPrimitives";
function projectSearchValue(environmentId: EnvironmentId, projectId: string): string {
  return `${environmentId}:${projectId}`;
}

function commaValues(value: string | undefined): ReadonlyArray<string> | undefined {
  const values = value
    ?.split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 10);
  return values?.length ? values : undefined;
}

function workspaceFilters(search: PullRequestsWorkspaceSearch): PullRequestListFilters | undefined {
  const labels = commaValues(search.labels)?.map((label) => [label]);
  const excludedLabels = commaValues(search.excludedLabels);
  const filters = {
    ...(search.draft === undefined ? {} : { draft: search.draft }),
    ...(search.review === undefined ? {} : { review: search.review }),
    ...(search.checks === undefined ? {} : { checks: search.checks }),
    ...(search.author === undefined ? {} : { author: search.author }),
    ...(labels === undefined ? {} : { labels }),
    ...(excludedLabels === undefined ? {} : { excludedLabels }),
  } satisfies PullRequestListFilters;
  return Object.keys(filters).length === 0 ? undefined : filters;
}

const INVOLVEMENT_TABS = [
  { value: "all", label: "All", Icon: LayersIcon },
  { value: "reviewing", label: "Reviewing", Icon: EyeIcon },
  { value: "authored", label: "Authored", Icon: PenLineIcon },
] as const satisfies ReadonlyArray<PullRequestFilterOption<PullRequestInvolvement>>;

const STATE_TABS = [
  { value: "all", label: "All", Icon: LayersIcon },
  { value: "open", label: "Open", Icon: GitPullRequestIcon },
  { value: "closed", label: "Closed", Icon: GitPullRequestClosedIcon },
  { value: "merged", label: "Merged", Icon: GitMergeIcon },
] as const satisfies ReadonlyArray<PullRequestFilterOption<PullRequestListState>>;

type PullRequestListSort = NonNullable<PullRequestsWorkspaceSearch["sort"]>;

const SORT_OPTIONS = [
  { value: "updated", label: "Recently updated", Icon: ClockIcon },
  { value: "oldest", label: "Oldest shown", Icon: CalendarArrowUpIcon },
  { value: "title", label: "Title", Icon: TypeIcon },
] as const satisfies ReadonlyArray<PullRequestFilterOption<PullRequestListSort>>;
export function PullRequestsWorkspace() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const projects = useProjects();
  const { environments } = useEnvironments();
  const listState = search.state ?? "open";
  const involvement = search.involvement ?? "all";
  const previousRows = useRef<ReturnType<typeof derivePullRequestWorkspaceView>["rows"]>([]);
  const pullRequestPanel = usePullRequestSurfaceStore((state) => state.panel);
  const openPullRequest = usePullRequestSurfaceStore((state) => state.open);
  const selectedPullRequestSurfaceId = usePullRequestSurfaceStore(
    (state) => state.panel.activeSurfaceId,
  );

  /**
   * Opens a pull request beside the list and says so in the address. Selection is replaced
   * rather than pushed: moving between pull requests is looking around one page, not a trail of
   * pages to step back through.
   */
  /** Whether a pull request is already open for the address, from a click or from a restore. */
  const restoredSelection = useRef(false);

  const selectPullRequest = useCallback(
    (pullRequest: EnvironmentPullRequestRow) => {
      // Opening from a row and restoring from the address must name the same surface. The host
      // is part of a surface's identity, so dropping it here opened #42 a second time the moment
      // the address effect read the selection this very click had just written.
      restoredSelection.current = true;
      openPullRequest(
        pullRequestSurface({
          environmentId: pullRequest.environmentId,
          projectId: pullRequest.projectId,
          ...(pullRequest.host === undefined ? {} : { host: pullRequest.host }),
          repository: pullRequest.repository,
          number: pullRequest.number,
          url: pullRequest.url,
        }),
      );
      void navigate({
        to: "/pull-requests",
        replace: true,
        search: (current) => ({
          ...current,
          selectedEnvironment: pullRequest.environmentId,
          selectedProject: pullRequest.projectId,
          selectedRepository: pullRequest.repository,
          selectedNumber: pullRequest.number,
          ...(pullRequest.host === undefined ? {} : { selectedHost: pullRequest.host }),
        }),
      });
    },
    [navigate, openPullRequest],
  );

  /** Hiding or closing the panel leaves the address saying nothing is selected. */
  const clearSelectedPullRequest = useCallback(() => {
    void navigate({
      to: "/pull-requests",
      replace: true,
      search: ({
        selectedEnvironment: _environment,
        selectedProject: _project,
        selectedRepository: _repository,
        selectedNumber: _number,
        selectedHost: _host,
        ...rest
      }) => rest,
    });
  }, [navigate]);

  // A reload restores the one pull request the address names. The panel's other tabs were
  // session state, and inventing them here would open work the reader did not ask for.
  useEffect(() => {
    if (restoredSelection.current) return;
    const { selectedRepository, selectedNumber, selectedEnvironment, selectedProject } = search;
    if (
      selectedRepository === undefined ||
      selectedNumber === undefined ||
      selectedEnvironment === undefined ||
      selectedProject === undefined
    ) {
      return;
    }
    // The address names the pull request, not where it lives. An address that did not carry a
    // host has one only if the selected Project still says which host it is checked out from;
    // assuming github.com would address a different pull request on an Enterprise install.
    const host =
      search.selectedHost ??
      projectPullRequestRepository(
        projects.find(
          (project) =>
            project.environmentId === selectedEnvironment && project.id === selectedProject,
        )?.repositoryIdentity,
      )?.host;
    if (host === undefined) return;
    restoredSelection.current = true;
    openPullRequest(
      pullRequestSurface({
        environmentId: EnvironmentId.make(selectedEnvironment),
        projectId: ProjectId.make(selectedProject),
        host,
        repository: selectedRepository,
        number: selectedNumber,
        url: `https://${host}/${selectedRepository}/pull/${selectedNumber}`,
      }),
    );
  }, [openPullRequest, projects, search]);

  const showPanel = usePullRequestSurfaceStore((state) => state.show);
  const hidePullRequestPanel = usePullRequestSurfaceStore((state) => state.hide);
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);

  /**
   * Showing the panel again puts back the pull request it was showing. Hiding is putting a
   * panel away, not deciding to look at something else, so the address says the same thing
   * afterwards as it did before.
   */
  const showPullRequestPanel = useCallback(() => {
    showPanel();
    const restored = usePullRequestSurfaceStore
      .getState()
      .panel.surfaces.find(
        (surface) => surface.id === usePullRequestSurfaceStore.getState().panel.activeSurfaceId,
      );
    if (restored === undefined) return;
    void navigate({
      to: "/pull-requests",
      replace: true,
      search: (current) => ({
        ...current,
        selectedEnvironment: restored.environmentId,
        selectedProject: restored.projectId,
        selectedRepository: restored.repository,
        selectedNumber: restored.number,
        ...(restored.host === undefined ? {} : { selectedHost: restored.host }),
      }),
    });
  }, [navigate, showPanel]);

  /**
   * This page has no ChatView, so it answers the shared panel shortcuts itself. Configured
   * bindings decide which keys these are; the page only says what they do here.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat) return;
      const target = event.target;
      // A shortcut must not fire out from under someone typing a search or a filter.
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement)
      ) {
        return;
      }
      const command = resolveShortcutCommand(event, keybindings);
      if (command === "rightPanel.toggle") {
        event.preventDefault();
        const panel = usePullRequestSurfaceStore.getState().panel;
        if (panel.surfaces.length === 0) return;
        if (panel.isOpen) hidePullRequestPanel();
        else showPullRequestPanel();
        return;
      }
      if (command === "pullRequest.copyReference") {
        const active = usePullRequestSurfaceStore.getState().panel;
        const surface = active.surfaces.find((entry) => entry.id === active.activeSurfaceId);
        if (surface === undefined) return;
        event.preventDefault();
        void navigator.clipboard?.writeText(surface.url);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hidePullRequestPanel, keybindings, showPullRequestPanel]);

  const selectedEnvironment = environments.find(
    (environment) => environment.environmentId === search.environment,
  );
  const projectFromSearch = projects.find(
    (project) => projectSearchValue(project.environmentId, project.id) === search.project,
  );
  const selectedProject =
    selectedEnvironment === undefined ||
    projectFromSearch?.environmentId === selectedEnvironment.environmentId
      ? projectFromSearch
      : undefined;
  const targetEnvironments = useMemo(
    () =>
      environments.filter(
        (environment) =>
          environment.connection.phase !== "ready" ||
          environment.serverConfig?.environment.capabilities.pullRequests === "list-stats-v1",
      ),
    [environments],
  );
  const targetEnvironmentIds = useMemo(
    () => targetEnvironments.map((environment) => environment.environmentId),
    [targetEnvironments],
  );
  const filters = useMemo(() => workspaceFilters(search), [search]);
  const listTargets = useMemo(
    () =>
      targetEnvironments.flatMap((environment) => {
        if (environment.connection.phase !== "ready") return [];
        if (environment.serverConfig?.environment.capabilities.pullRequests !== "list-stats-v1") {
          return [];
        }
        const projectIds = projects
          .filter((project) => project.environmentId === environment.environmentId)
          .map((project) => project.id);
        const input = {
          state: listState,
          involvement,
          projectIds,
          ...(search.q === undefined ? {} : { query: search.q }),
          ...(filters === undefined ? {} : { filters }),
          limit: 50,
        } satisfies PullRequestListInput;
        return [{ environmentId: environment.environmentId, input }];
      }),
    [filters, projects, involvement, listState, search.q, targetEnvironments],
  );
  const listQuery = usePullRequestLists(listTargets);
  const answers = useMemo<ReadonlyArray<PullRequestEnvironmentAnswer>>(
    () => listQuery.values.map(([environmentId, result]) => ({ environmentId, result })),
    [listQuery.values],
  );
  const environmentFailures = useMemo<ReadonlyArray<PullRequestEnvironmentFailure>>(() => {
    const transport = listQuery.failures.map(([environmentId, message]) => ({
      environmentId,
      message,
      stale: false,
    }));
    const failedIds = new Set(transport.map((failure) => failure.environmentId));
    return [
      ...transport,
      ...targetEnvironments.flatMap((environment) =>
        environment.connection.phase === "unavailable" && !failedIds.has(environment.environmentId)
          ? [
              {
                environmentId: environment.environmentId,
                message:
                  environment.connection.error ??
                  `${environment.label} is unavailable. Reconnect it and retry.`,
                stale: true,
              } as const,
            ]
          : [],
      ),
    ];
  }, [listQuery.failures, targetEnvironments]);
  const snapshotKey = targetEnvironmentIds.join(",");
  const snapshot = useMemo(
    () =>
      readPullRequestWorkspaceSnapshot(
        typeof window === "undefined" ? undefined : window.localStorage,
        targetEnvironmentIds,
      ),
    // Re-read when the connected workspace changes; storage keeps only the latest full snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [snapshotKey],
  );
  const workspaceInput = {
    targetEnvironmentIds,
    answers,
    environmentFailures,
    pending:
      listQuery.isPending ||
      targetEnvironments.some(
        (environment) =>
          environment.connection.phase === "starting" ||
          environment.connection.phase === "synchronizing",
      ),
    filters: {
      ...(selectedEnvironment === undefined
        ? {}
        : { environmentId: selectedEnvironment.environmentId }),
      ...(selectedProject === undefined ? {} : { projectId: selectedProject.id }),
      state: listState,
      involvement,
      ...(search.q === undefined ? {} : { query: search.q }),
      ...(filters === undefined ? {} : { filters }),
    },
    previousRows: previousRows.current,
    snapshot,
  } as const;
  const {
    environmentId: _presentationEnvironmentId,
    projectId: _presentationProjectId,
    ...aggregateFilters
  } = workspaceInput.filters;
  const aggregateWorkspaceInput = { ...workspaceInput, filters: aggregateFilters };
  const aggregateRowView = derivePullRequestWorkspaceView(aggregateWorkspaceInput);
  const statsTargets = useMemo(
    () =>
      targetEnvironmentIds.flatMap((environmentId) => {
        const refs = aggregateRowView.rows
          .filter((row) => row.environmentId === environmentId && row.authority === "live")
          .map((row) => ({
            projectId: row.projectId,
            repository: row.repository,
            number: row.number,
          }));
        return refs.length === 0 ? [] : [{ environmentId, input: { refs } }];
      }),
    [aggregateRowView.rows, targetEnvironmentIds],
  );
  const statsQuery = usePullRequestListStats(statsTargets);
  const statistics = useMemo(
    () =>
      statsQuery.values.flatMap(([environmentId, result]) =>
        result.stats.map((stat) => ({ ...stat, environmentId })),
      ),
    [statsQuery.values],
  );
  const aggregateView = derivePullRequestWorkspaceView({
    ...aggregateWorkspaceInput,
    statistics,
    statisticsPending: statsTargets.length > 0 && statsQuery.isPending,
  });
  const view = derivePullRequestWorkspaceView({
    ...workspaceInput,
    previousRows: aggregateView.rows,
    statistics,
    statisticsPending: aggregateView.statisticsPending,
  });

  useEffect(() => {
    previousRows.current = aggregateView.rows;
  }, [aggregateView.rows]);
  useEffect(() => {
    if (listQuery.isPending || !aggregateView.rows.some((row) => row.authority === "live")) {
      return;
    }
    writePullRequestWorkspaceSnapshot(
      typeof window === "undefined" ? undefined : window.localStorage,
      {
        environmentIds: targetEnvironmentIds,
        rows: aggregateView.rows,
        ...(search.q === undefined ? {} : { query: search.q }),
        savedAt: new Date().toISOString(),
      },
    );
  }, [aggregateView.rows, listQuery.isPending, search.q, targetEnvironmentIds]);

  type SearchPatch = {
    [Key in keyof PullRequestsWorkspaceSearch]?: PullRequestsWorkspaceSearch[Key] | undefined;
  };
  const updateSearch = (patch: SearchPatch) => {
    const next = Object.fromEntries(
      Object.entries({ ...search, ...patch }).filter(([, value]) => value !== undefined),
    ) as PullRequestsWorkspaceSearch;
    return void navigate({
      to: "/pull-requests",
      search: next,
      replace: true,
    });
  };
  // rollup, so its glyph stays and simply does not open.
  const checksReadableEnvironments = new Set(
    targetEnvironments
      .filter(
        (environment) =>
          environment.serverConfig?.environment.capabilities.pullRequestChecksDetail !== undefined,
      )
      .map((environment) => environment.environmentId),
  );
  const environmentLabels = new Map(
    environments.map((environment) => [environment.environmentId, environment.label]),
  );
  const refresh = () => {
    listQuery.refresh();
    statsQuery.refresh();
  };

  // What this list can be narrowed to, read from the rows the Environments actually answered
  // with rather than from a free-text box that narrows to nothing when it is misspelled.
  const facets = useMemo(
    () => collectPullRequestListFacets(aggregateView.rows, listState),
    [aggregateView.rows, listState],
  );
  // AFAR Projects whose repository could not be read this time round, named where the reader is
  // already choosing between Projects.
  const unavailableProjects = useMemo(() => {
    const entries = new Map<string, string>();
    for (const failure of view.failures) {
      if (failure.projectId === null) continue;
      entries.set(
        pullRequestProjectKey({ id: failure.projectId, environmentId: failure.environmentId }),
        failure.failure.message,
      );
    }
    return entries;
  }, [view.failures]);
  const environmentOptions: ReadonlyArray<PullRequestFilterOption<string>> = [
    { value: "", label: "All Environments", Icon: LayersIcon },
    ...environments.map((environment) => ({
      value: String(environment.environmentId),
      label: environment.label,
      Icon: LayersIcon,
    })),
  ];
  const sort = search.sort ?? "updated";
  const rows = useMemo(
    () =>
      [...view.rows].sort((left, right) =>
        sort === "title"
          ? left.title.localeCompare(right.title)
          : sort === "oldest"
            ? left.updatedAt.localeCompare(right.updatedAt)
            : right.updatedAt.localeCompare(left.updatedAt),
      ),
    [sort, view.rows],
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const searchInput = (
    <PullRequestSearchInput
      busy={listQuery.isPending}
      onChange={(next) => updateSearch({ q: next || undefined })}
      value={search.q ?? ""}
    />
  );
  const sortMenu = (
    <CompactFilterMenu
      label="Sort Pull Requests"
      onChange={(next) => updateSearch({ sort: next === "updated" ? undefined : next })}
      options={SORT_OPTIONS}
      outlined
      triggerIcon={<ArrowDownUpIcon aria-hidden className="size-4" />}
      triggerLabel="Sort"
      value={sort}
    />
  );
  const filtersMenu = (
    <PullRequestFiltersMenu
      authorOptions={facets.authors}
      environment={selectedEnvironment?.environmentId}
      environmentOptions={environmentOptions}
      filters={filters ?? {}}
      involvement={involvement}
      involvementOptions={INVOLVEMENT_TABS}
      labelOptions={facets.labels}
      onEnvironment={(environment) =>
        // Narrowing to one Environment drops an AFAR Project chosen in another, which would
        // otherwise narrow the list to nothing and say the filters were to blame.
        updateSearch({ environment, project: undefined })
      }
      onFilters={(next) =>
        updateSearch({
          draft: next.draft,
          review: next.review,
          checks: next.checks,
          author: next.author,
          labels: next.labels?.flatMap((group) => group).join(","),
        })
      }
      onInvolvement={(next) => updateSearch({ involvement: next })}
      onProject={(projectId, environmentId) =>
        updateSearch(
          projectId === undefined || environmentId === undefined
            ? { project: undefined }
            : { project: projectSearchValue(environmentId, projectId) },
        )
      }
      onState={(next) => updateSearch({ state: next })}
      projectEnvironmentId={selectedProject?.environmentId}
      projectId={selectedProject?.id}
      projects={projects}
      state={listState}
      stateOptions={STATE_TABS}
      unavailable={unavailableProjects}
    />
  );

  const listBody = (
    <PullRequestList
      checksReadableEnvironments={checksReadableEnvironments}
      environmentLabels={environmentLabels}
      onClearQuery={() => updateSearch({ q: undefined })}
      onOpenDetails={(pullRequest) => selectPullRequest(pullRequest)}
      onRetry={refresh}
      query={search.q ?? ""}
      selectedSurfaceId={selectedPullRequestSurfaceId}
      view={{ ...view, rows }}
    />
  );

  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <WorkspaceShellMain className="relative flex min-h-0 flex-1 flex-row overflow-hidden p-0">
        <PullRequestsColumn
          filtersMenu={filtersMenu}
          involvement={involvement}
          listBody={listBody}
          onInvolvement={(next) => updateSearch({ involvement: next })}
          onRefresh={refresh}
          onState={(next) => updateSearch({ state: next })}
          panelControls={
            <PanelLayoutControls
              onToggleRightPanel={() =>
                pullRequestPanel.isOpen ? hidePullRequestPanel() : showPullRequestPanel()
              }
              onToggleTerminal={() => undefined}
              rightPanelAvailable={pullRequestPanel.surfaces.length > 0}
              rightPanelOpen={pullRequestPanel.isOpen}
              rightPanelShortcutLabel={shortcutLabelForCommand(keybindings, "rightPanel.toggle")}
              rightPanelUnavailableLabel="Select a Pull Request first"
              showTerminalControl={false}
              terminalAvailable={false}
              terminalOpen={false}
              terminalShortcutLabel={null}
            />
          }
          refreshing={listQuery.isPending}
          scrollRef={scrollRef}
          searchInput={searchInput}
          searchValue={search.q ?? ""}
          sortMenu={sortMenu}
          state={listState}
        />
        <PullRequestSurfacePanel onSelectionCleared={clearSelectedPullRequest} />
      </WorkspaceShellMain>
    </SidebarInset>
  );
}

/**
 * The search, folded away into its icon until it is wanted. Following T3Code: it lives in the
 * condensed top bar, where the scarce horizontal space belongs to the filters until the reader
 * asks to type.
 */
function ExpandableSearch({
  searchInput,
  searchValue,
  open,
  onOpenChange,
  focusToken,
  onFocusWithin,
}: {
  readonly searchInput: ReactNode;
  readonly searchValue: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Bumped to pull focus into the input while it is already showing — the Mod+F path. */
  readonly focusToken: number;
  /**
   * Focus entering and leaving the expanded input. An unmount fires no blur, which is the point:
   * whoever unmounted this can still see the reader was mid-typing and move the focus somewhere
   * that continues the sentence.
   */
  readonly onFocusWithin?: (focused: boolean) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    containerRef.current?.querySelector("input")?.focus();
  }, [open]);
  const appliedFocusToken = useRef(focusToken);
  useEffect(() => {
    if (appliedFocusToken.current === focusToken) return;
    appliedFocusToken.current = focusToken;
    const input = containerRef.current?.querySelector("input");
    input?.focus();
    input?.select();
  }, [focusToken]);
  if (open || searchValue.length > 0) {
    return (
      <div
        className="w-56 min-w-24 shrink"
        onBlur={() => {
          onFocusWithin?.(false);
          if (searchValue.length === 0) onOpenChange(false);
        }}
        onFocus={() => onFocusWithin?.(true)}
        ref={containerRef}
      >
        {searchInput}
      </div>
    );
  }
  return (
    <Button
      aria-label="Search Pull Requests"
      onClick={() => onOpenChange(true)}
      size="icon-sm"
      variant="ghost"
    >
      <SearchIcon className="size-4" />
    </Button>
  );
}

function PullRequestRefreshControl({
  compact = false,
  refreshing,
  onRefresh,
}: {
  readonly compact?: boolean;
  readonly refreshing: boolean;
  readonly onRefresh: () => void;
}) {
  return (
    <Button
      aria-label="Refresh Pull Requests"
      disabled={refreshing}
      onClick={onRefresh}
      size={compact ? "icon-sm" : "icon"}
      variant={compact ? "ghost" : "outline"}
    >
      <RefreshCwIcon aria-hidden className={cn("size-4", refreshing && "animate-spin")} />
    </Button>
  );
}

/**
 * The Pull Request list column, following T3Code. The full controls live at the top of the
 * scroll flow; once they scroll away the title transforms into the scope itself — "Pull Requests
 * / Open ▾ Authored ▾" — where each segment is the menu for that filter, and a folded search
 * sits on the right. Scrolled back up, the top bar returns to the plain title.
 */
function PullRequestsColumn({
  refreshing,
  onRefresh,
  panelControls,
  searchValue,
  involvement,
  state,
  onInvolvement,
  onState,
  searchInput,
  sortMenu,
  filtersMenu,
  listBody,
  scrollRef,
}: {
  readonly refreshing: boolean;
  readonly onRefresh: () => void;
  /**
   * The shared right-panel toggle, following T3Code: one control in one place, present whether
   * the panel is showing or put away, rather than a button that only appears once it is hidden.
   */
  readonly panelControls: ReactNode;
  readonly searchValue: string;
  readonly involvement: PullRequestInvolvement;
  readonly state: PullRequestListState;
  readonly onInvolvement: (involvement: PullRequestInvolvement) => void;
  readonly onState: (state: PullRequestListState) => void;
  readonly searchInput: ReactNode;
  readonly sortMenu: ReactNode;
  readonly filtersMenu: ReactNode;
  readonly listBody: ReactNode;
  readonly scrollRef: RefObject<HTMLDivElement | null>;
}) {
  const markerRef = useRef<HTMLDivElement | null>(null);
  const [condensed, setCondensed] = useState(false);
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;
    const observer = new IntersectionObserver(
      ([entry]) => setCondensed(entry ? !entry.isIntersecting : false),
      { root: scrollRef.current },
    );
    observer.observe(marker);
    return () => observer.disconnect();
  }, [scrollRef]);
  // Typing into the top bar's search narrows the list, and a short enough list un-scrolls the
  // page — which dissolves the condensed top bar and unmounts the very input being typed in. The
  // two inputs are one search to the reader, so the focus follows the value into the in-flow bar,
  // caret at the end, and the sentence continues.
  const topbarSearchFocusedRef = useRef(false);
  const inFlowSearchRef = useRef<HTMLDivElement | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchFocusToken, setSearchFocusToken] = useState(0);
  const searchExpanded = searchOpen || searchValue.length > 0;
  // Mod+F belongs to this page's own search: the desktop shell binds no find-in-page, so the
  // shortcut would otherwise do nothing. Condensed, it unfolds the top bar's search; at the top,
  // it focuses the in-flow bar and selects the query the way a find field would.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key.toLowerCase() !== "f" || !(event.metaKey || event.ctrlKey)) return;
      if (event.altKey || event.shiftKey) return;
      event.preventDefault();
      if (condensed) {
        setSearchOpen(true);
        setSearchFocusToken((token) => token + 1);
        return;
      }
      const input = inFlowSearchRef.current?.querySelector("input");
      input?.focus();
      input?.select();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [condensed]);
  useEffect(() => {
    if (condensed) return;
    // The fold-out is gone from the chrome; forgetting it open would start the next condensing
    // with an empty expanded search nobody asked for.
    setSearchOpen(false);
    if (!topbarSearchFocusedRef.current) return;
    topbarSearchFocusedRef.current = false;
    const input = inFlowSearchRef.current?.querySelector("input");
    if (!input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [condensed]);

  return (
    // Painted flat like the chat column: the inset underneath carries the chrome grain, and a
    // content surface that lets it show reads as a different background than every thread.
    <div className="@container/pr-list flex min-h-0 min-w-0 flex-1 flex-col bg-background">
      <WorkspacePageHeader className="relative bg-background" electron={isElectron}>
        {condensed ? (
          <WorkspaceBreadcrumb ariaLabel="Pull Request scope" className="overflow-hidden">
            {/* An expanded search owns the scarce horizontal space. The page title stays
                available to readers while the live filters remain available in both states. */}
            <WorkspaceBreadcrumbItem className={cn(searchExpanded && "sr-only")} current>
              <h1 className="truncate">Pull Requests</h1>
            </WorkspaceBreadcrumbItem>
            {searchExpanded ? null : <WorkspaceBreadcrumbSeparator />}
            <WorkspaceBreadcrumbItem className="shrink gap-1.5">
              <CompactFilterMenu
                className="shrink-0"
                label="Filter by state"
                onChange={onState}
                options={STATE_TABS}
                value={state}
              />
              <CompactFilterMenu
                label="Filter by involvement"
                onChange={onInvolvement}
                options={INVOLVEMENT_TABS}
                value={involvement}
              />
            </WorkspaceBreadcrumbItem>
          </WorkspaceBreadcrumb>
        ) : (
          <WorkspaceBreadcrumb ariaLabel="Pull Requests breadcrumb">
            <WorkspaceBreadcrumbItem current>
              <h1 className="truncate">Pull Requests</h1>
            </WorkspaceBreadcrumbItem>
          </WorkspaceBreadcrumb>
        )}
        <div className="min-w-0 flex-1" />
        <div className="flex shrink items-center gap-1.5">
          {condensed ? (
            <>
              <ExpandableSearch
                focusToken={searchFocusToken}
                onFocusWithin={(focused) => {
                  topbarSearchFocusedRef.current = focused;
                }}
                onOpenChange={setSearchOpen}
                open={searchOpen}
                searchInput={searchInput}
                searchValue={searchValue}
              />
              <PullRequestRefreshControl compact onRefresh={onRefresh} refreshing={refreshing} />
            </>
          ) : null}
          {panelControls}
        </div>
      </WorkspacePageHeader>

      <div
        className="topbar-scroll-fade scrollbar-gutter-both min-h-0 flex-1 overflow-y-auto"
        ref={scrollRef}
      >
        <WorkspacePageContainer className="min-h-full gap-4" width="expanded">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2" ref={inFlowSearchRef}>
              <div className="min-w-0 basis-full @lg/pr-list:basis-0 @lg/pr-list:flex-1">
                {searchInput}
              </div>
              {sortMenu}
              {filtersMenu}
              {!condensed ? (
                <PullRequestRefreshControl onRefresh={onRefresh} refreshing={refreshing} />
              ) : null}
            </div>
            {/* Scrolled past this marker, the controls are gone and the title takes over. */}
            <div aria-hidden className="-mt-3 h-px w-full" ref={markerRef} />
          </div>

          {listBody}
        </WorkspacePageContainer>
      </div>
    </div>
  );
}
